import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AnimeCard from "@/components/feature/AnimeCard";
import EmptyState from "@/components/base/EmptyState";
import LoadingState from "@/components/base/LoadingState";
import { useSeo } from "@/hooks/useSeo";
import { animeList } from "@/mocks/anime";

function matchAnime(keyword: string) {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return animeList.filter((a) => {
    const haystack = [a.title, a.region, a.status, a.director, ...a.categories, a.description]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw) || a.categories.some((c) => c.toLowerCase().includes(kw));
  });
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(queryParam);
  const [loading, setLoading] = useState(false);

  useSeo(
    queryParam ? `搜索“${queryParam}” - 樱花动漫在线` : "搜索动漫 - 樱花动漫在线",
    `在樱花动漫在线搜索动漫资源，关键词：${queryParam || "全部动漫"}，快速找到你想看的动漫作品。`
  );

  useEffect(() => {
    setKeyword(queryParam);
  }, [queryParam]);

  useEffect(() => {
    if (!queryParam) return;
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(timer);
  }, [queryParam]);

  const results = useMemo(() => matchAnime(queryParam), [queryParam]);
  const recommend = useMemo(() => [...animeList].sort((a, b) => b.views - a.views).slice(0, 4), []);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams();
    if (keyword.trim()) next.set("q", keyword.trim());
    setSearchParams(next);
  };

  return (
    <div className="w-full">
      <div className="w-full bg-primary-50 border-b border-primary-100">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-600 mb-3 flex-wrap">
            <Link to="/" className="hover:text-primary-700 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900 font-medium">搜索</span>
          </nav>
          <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-2">搜索动漫</h1>
          <p className="text-sm md:text-base text-foreground-700 mb-6 max-w-2xl leading-relaxed">
            输入动漫名称、类型或关键词，快速定位你想看的作品。
          </p>

          <form onSubmit={submitSearch} className="w-full max-w-2xl flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base">
                <i className="ri-search-line"></i>
              </span>
              <input
                type="text"
                name="q"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="例如：热血、恋爱、星海征途…"
                className="w-full h-11 pl-10 pr-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-search-2-line"></i>
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        {!queryParam ? (
          <EmptyState
            icon="ri-search-eye-line"
            title="输入关键词开始搜索"
            description="支持按动漫名称、类型标签、地区或简介关键词进行模糊搜索。"
          />
        ) : loading ? (
          <LoadingState label={`正在搜索“${queryParam}”…`} />
        ) : results.length === 0 ? (
          <div>
            <EmptyState
              icon="ri-emotion-sad-line"
              title={`没有找到与“${queryParam}”相关的结果`}
              description="换个关键词试试，或者看看下面这些热门推荐。"
            />
            <div className="mt-6">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4">你可能感兴趣</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {recommend.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} showDescription={false} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-6 flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="text-sm text-foreground-700">
                搜索词：
                <span className="font-bold text-accent-600">“{queryParam}”</span>
              </p>
              <span className="text-foreground-300">·</span>
              <p className="text-sm text-foreground-700">
                共找到 <span className="font-bold text-primary-700">{results.length}</span> 条结果
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {results.map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}